<template>
  <layouts-guest>
    <base-shadow-card :title="t('reset_password.title')">
      <form @submit.prevent="onSubmit">
        <div class="mb-4">
          <label for="email" class="form-label">{{ t('word.email') }}</label>
          <input
            v-model="email"
            id="email"
            name="email"
            type="email"
            :placeholder="t('word.email')"
            :class="`form-input ${errors['email'] ? '!border-red-500' : ''}`"
          >
          <p class="form-error-text">{{ errors['email'] }}</p>
        </div>
        <div class="mb-4">
          <label for="password" class="form-label">{{ t('word.password_new') }}</label>
          <input
            v-model="password"
            id="password"
            name="password"
            type="password"
            :placeholder="t('word.password_new')"
            :class="`form-input ${errors['password'] ? '!border-red-500' : ''}`"
          >
          <p class="form-error-text">{{ errors['password'] }}</p>
        </div>
        <div class="mb-4">
          <label for="password_confirmation" class="form-label">{{ t('word.password_confirmation') }}</label>
          <input
            v-model="password_confirmation"
            id="password_confirmation"
            name="password_confirmation"
            type="password"
            :placeholder="t('word.password_confirmation')"
            :class="`form-input ${errors['password_confirmation'] ? '!border-red-500' : ''}`"
          >
          <p class="form-error-text">{{ errors['password_confirmation'] }}</p>
        </div>
        <div class="flex items-center justify-between pt-2">
          <button @click="router.back()" type="button" class="bg-gray-300 hover:bg-gray-400 text-gray-800 py-1.5 px-4 rounded">
            {{ t('word.back') }}
          </button>
          <base-form-submit :disabled="btnDisabled" />
        </div>
      </form>
    </base-shadow-card>
  </layouts-guest>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { axios } from '@/plugins/axios'
import { useRouter, useRoute } from 'vue-router'
import { useField, useForm } from 'vee-validate'
import * as yup from 'yup'
import { useStore } from '@/store/index'
import { ALERT_COLORS } from '@/store/base'

import LayoutsGuest from '@/layouts/Guest.vue'
import BaseShadowCard from '@/components/BaseShadowCard.vue'
import BaseFormSubmit from '@/components/BaseFormSubmit.vue'

export default defineComponent({
  components: { LayoutsGuest, BaseShadowCard, BaseFormSubmit },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const route = useRoute()
    const store = useStore()
    const btnDisabled = ref(false)

    const schema = yup.object({
      email: yup.string().required().email().max(255),
      password: yup.string().required().min(8).max(255),
      password_confirmation: yup.string().required().oneOf([yup.ref('password'), null], t('validation.password_confirmation')),
    })
    const { handleSubmit, errors } = useForm({ validationSchema: schema })
    const { value: email } = useField<string>('email')
    const { value: password } = useField<string>('password')
    const { value: password_confirmation } = useField<string>('password_confirmation')

    onMounted(() => {
      email.value = window.atob(route.query.email as string)
    })

    const onSubmit = handleSubmit(values => {
      btnDisabled.value = true
      axios
        .post('/reset-password', {
          email: values.email,
          password: values.password,
          token: route.params.token
        })
        .then(() => {
          store.commit('base/setAlertMessages', { messages: [t('reset_password.success')], persistent: true, color: ALERT_COLORS.info })
          router.push('/login')
        })
        .finally(() => btnDisabled.value = false)
    })

    return {
      t,
      errors,
      email,
      password,
      password_confirmation,
      onSubmit,
      btnDisabled,
      router
    }
  },
})
</script>
